import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, useCancelDeletion } from '../hooks/api/useUsers';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Chip,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { PageLayout } from '../components/ui/PageLayout';

import { User, UserRole, UserCreate } from '../api';
import { log } from '../utils/errors/logger';
import { isValidEmail, isNotEmpty, passwordsMatch } from '../utils/security/validators';
import { formatDateTime } from '../utils/formatting/formatters';
import { useSystem } from '../hooks/api/config';
import { useSnackbar } from '@/contexts/SnackbarContext';

/**
 * Calculate remaining days in grace period
 * @param deletedAt - ISO string of deletion timestamp
 * @returns Number of days remaining (rounded up)
 */
const calculateRemainingDays = (deletedAt: string | null | undefined): number => {
  if (!deletedAt) {return 0;}
  const deletionDate = new Date(deletedAt);
  const expiryDate = new Date(deletionDate.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
};

const Users: React.FC = () => {
  const { t, i18n } = useTranslation();
  useSystem();
  const { showSuccess, showError } = useSnackbar();
  
  // --- Local UI State ---
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced search term: the input updates searchTerm on every keystroke (so
  // the field stays responsive), but the server-side query only fires on the
  // settled value -- avoiding an API request + full table re-render per char.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // --- TanStack Query Hooks with server-side pagination ---
  const { data, isLoading: isLoadingUsers, isError: isUsersError } = useUsers({
    skip: page * rowsPerPage,
    limit: rowsPerPage,
    search: debouncedSearch || undefined
  });

  // The admin user-management list always receives full User items (the API
  // returns the trimmed UserBasicInfo only to non-admin callers, and this page
  // is admin-gated), so narrow the union to User for the admin columns.
  const users = (data?.users || []) as User[];
  const totalUsers = data?.total || 0;

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const cancelDeletionMutation = useCancelDeletion();
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDeletionDialogOpen, setCancelDeletionDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    email: '',
    role: 'standard',
    active: true,
    email_verified: false
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (isUsersError) {
      showError(t('user.error'));
      log.error('Error fetching users', { error: isUsersError });
    }
  }, [isUsersError, showError, t]);

  useEffect(() => {
    if (createUserMutation.isSuccess) {
      showSuccess(t('user.userAdded'));
      setFormOpen(false);
      setFormData({
        username: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        email: '',
        role: 'standard',
        active: true,
        email_verified: false
      });
      setFormErrors({});
      setCurrentUser(null);
      setIsEditMode(false);
      // Reset mutation to clear success state
      createUserMutation.reset();
    }
    if (createUserMutation.isError) {
      showError(t('user.error'));
      log.error('Error creating user', { error: createUserMutation.error });
    }
  }, [createUserMutation.isSuccess, createUserMutation.isError, createUserMutation, showSuccess, showError, t]);

  useEffect(() => {
    if (updateUserMutation.isSuccess) {
      showSuccess(t('user.userUpdated'));
      setFormOpen(false);
      setFormData({
        username: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        email: '',
        role: 'standard',
        active: true,
        email_verified: false
      });
      setFormErrors({});
      setCurrentUser(null);
      setIsEditMode(false);
      // Reset mutation to clear success state
      updateUserMutation.reset();
    }
    if (updateUserMutation.isError) {
      showError(t('user.error'));
      log.error('Error updating user', { error: updateUserMutation.error });
    }
  }, [updateUserMutation.isSuccess, updateUserMutation.isError, updateUserMutation, showSuccess, showError, t]);

  useEffect(() => {
    if (deleteUserMutation.isSuccess) {
      showSuccess(t('user.userDeleted'));
      setDeleteDialogOpen(false);
      setCurrentUser(null);
      // Reset mutation to clear success state
      deleteUserMutation.reset();
    }
    if (deleteUserMutation.isError) {
      showError(t('user.error'));
      log.error('Error deleting user', { error: deleteUserMutation.error });
    }
  }, [deleteUserMutation.isSuccess, deleteUserMutation.isError, deleteUserMutation, showSuccess, showError, t]);

  useEffect(() => {
    if (cancelDeletionMutation.isSuccess) {
      showSuccess(t('users.deletionCancelled'));
      setCancelDeletionDialogOpen(false);
      setCurrentUser(null);
      // Reset mutation to clear success state
      cancelDeletionMutation.reset();
    }
    if (cancelDeletionMutation.isError) {
      showError(t('user.error'));
      log.error('Error canceling deletion', { error: cancelDeletionMutation.error });
    }
  }, [cancelDeletionMutation.isSuccess, cancelDeletionMutation.isError, cancelDeletionMutation, showSuccess, showError, t]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      email: '',
      role: 'standard',  // Updated to match API roles: 'admin', 'premium', 'employee', 'standard'
      active: true,
      email_verified: false
    });
    setFormErrors({});
    setCurrentUser(null);
    setIsEditMode(false);
  };

  const handleOpenForm = (user: User | null = null) => {
    resetForm();
    if (user) {
      setFormData({
        username: user.username || '',
        password: '',  // Don't populate password for security reasons
        confirmPassword: '',
        fullName: user.full_name || '',
        email: user.email || '',
        role: user.role || 'standard',
        active: user.active !== undefined ? user.active : true,
        email_verified: user.email_verified || false
      });
      setCurrentUser(user);
      setIsEditMode(true);
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleOpenDeleteDialog = (user: User) => {
    setCurrentUser(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCurrentUser(null);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!isNotEmpty(formData.username)) {
      errors.username = t('user.required');
    }

    if (!isEditMode && !formData.password) {
      errors.password = t('user.required');
    }

    if (!isEditMode && !passwordsMatch(formData.password, formData.confirmPassword)) {
      errors.confirmPassword = t('user.passwordMismatch');
    }

    // Email ist ein Pflichtfeld
    if (!formData.email || !isNotEmpty(formData.email)) {
      errors.email = t('user.required');
    } else if (!isValidEmail(formData.email)) {
      errors.email = t('user.invalidEmail');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {return;}

    const userData: Partial<User> & { password?: string } = {
      username: formData.username,
      full_name: formData.fullName,
      email: formData.email,
      role: formData.role as UserRole,
      active: formData.active,
      email_verified: formData.email_verified
    };

    if (formData.password) {
      userData.password = formData.password;
    }

    if (isEditMode && currentUser) {
      updateUserMutation.mutate({ username: currentUser.username, data: userData });
    } else {
      const createPayload: UserCreate = {
        username: formData.username,
        email: formData.email,
        full_name: formData.fullName,
        role: formData.role as UserRole,
        password: formData.password || '',
      };
      createUserMutation.mutate(createPayload);
    }
  };

  const handleDeleteUser = () => {
    if (!currentUser) {return;}
    deleteUserMutation.mutate(currentUser.username);
  };

  const handleOpenCancelDeletionDialog = (user: User) => {
    setCurrentUser(user);
    setCancelDeletionDialogOpen(true);
  };

  const handleCloseCancelDeletionDialog = () => {
    setCancelDeletionDialogOpen(false);
    setCurrentUser(null);
  };

  const handleCancelDeletion = () => {
    if (!currentUser) {return;}
    cancelDeletionMutation.mutate(currentUser.username);
  };

  // Funktion zum Umschalten des Aktivierungsstatus eines Benutzers
  const toggleUserStatus = (user: User) => {
    updateUserMutation.mutate({ username: user.username, data: { active: !user.active } });
  };

  // Header actions for PageLayout
  const headerActions = (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={() => handleOpenForm()}
      sx={{
        backgroundColor: '#003064',  // Corporate blue
        '&:hover': {
          backgroundColor: '#00204d',  // Darker blue
        },
      }}
    >
      {t('user.addUser')}
    </Button>
  );

  return (
    <>
      <PageLayout
        title={t('user.management')}
        isLoading={false}
        maxWidth="xl"
        headerActions={headerActions}
      >
        <Box sx={{ mb: 3 }}>
          <TextField
            label={t('user.search')}
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: '300px' }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.username')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.fullName')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.email')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.emailVerified')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.role')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.status')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.created')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.lastLogin')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{t('user.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingUsers ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress sx={{ color: '#003064' }} /> {/* Corporate blue */}
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((user, index) => (
                  <TableRow key={user.id || `${user.username}-${index}`} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>
                      {user.email_verified ? (
                        <Box component="span" sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                          <CheckIcon fontSize="small" sx={{ mr: 0.5 }} /> {t('user.emailStatus.verified')}
                        </Box>
                      ) : (
                        <Box component="span" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '20px', marginRight: '4px' }}>!</span> {t('user.emailStatus.notVerified')}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.role ? t(`user.roles.${user.role.toLowerCase()}`) : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {user.active ?
                            <Box component="span" sx={{ color: 'success.main', mr: 1 }}>
                              {t('user.statuses.active')}
                            </Box> :
                            <Box component="span" sx={{ color: 'text.disabled', mr: 1 }}>
                              {t('user.statuses.inactive')}
                            </Box>
                          }
                          <Button
                            size="small"
                            variant="outlined"
                            color={user.active ? "error" : "success"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleUserStatus(user);
                            }}
                            sx={{ ml: 1, minWidth: '80px' }}
                          >
                            {user.active ? t('user.deactivate') : t('user.activate')}
                          </Button>
                        </Box>
                        {(user as User & { deleted_at?: string | null }).deleted_at && (() => {
                          const deletedAt = (user as User & { deleted_at?: string | null }).deleted_at;
                          const daysRemaining = calculateRemainingDays(deletedAt);
                          return daysRemaining > 0 ? (
                            <Tooltip title={t('users.gracePeriodExplanation')}>
                              <Chip
                                label={`${t('users.deletionScheduled')} (${t('users.daysRemaining', { count: daysRemaining })})`}
                                color="warning"
                                size="small"
                                sx={{ width: '100%' }}
                              />
                            </Tooltip>
                          ) : null;
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDateTime(user.created_at, i18n.language)}</TableCell>
                    <TableCell>{formatDateTime((user as User & { last_login?: string | null }).last_login, i18n.language)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenForm(user)}
                        sx={{ color: '#003064' }} // Corporate blue
                      >
                        <EditIcon />
                      </IconButton>
                      {(user as User & { deleted_at?: string | null }).deleted_at && calculateRemainingDays((user as User & { deleted_at?: string | null }).deleted_at) > 0 ? (
                        <Tooltip title={t('users.cancelDeletion')}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenCancelDeletionDialog(user)}
                            sx={{ color: 'success.main' }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(user)}
                          sx={{ color: '#E19E19' }} // Corporate orange
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {t('user.noUsers')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      </PageLayout>

      {/* Add/Edit User Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: '#003064', 
          color: 'white',
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif'
        }}>
          {isEditMode ? t('user.editUser') : t('user.addUser')}
        </DialogTitle>
	        <form onSubmit={(e) => { void handleSubmitForm(e); }}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              margin="dense"
              label={t('user.username')}
              name="username"
              value={formData.username}
              onChange={handleFormChange}
              fullWidth
              required
              disabled={isEditMode} // Can't change username in edit mode
              error={Boolean(formErrors.username)}
              helperText={formErrors.username}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="dense"
              label={t('user.fullName')}
              name="fullName"
              value={formData.fullName}
              onChange={handleFormChange}
              fullWidth
              sx={{ mb: 2 }}
            />

            <TextField
              margin="dense"
              label={t('user.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              fullWidth
              required
              error={Boolean(formErrors.email)}
              helperText={formErrors.email}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
              <InputLabel>{t('user.role')}</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={(event: SelectChangeEvent<string>) => {
                  setFormData({
                    ...formData,
                    role: event.target.value,
                  });
                }}
                label={t('user.role')}
              >
                {Object.values(UserRole).map((role) => (
                  <MenuItem key={role} value={role}>
                    {t(`user.roles.${role}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
              <InputLabel>{t('user.status')}</InputLabel>
              <Select
                name="active"
                value={formData.active}
                onChange={(e) => setFormData({
                  ...formData,
                  active: e.target.value === 'true'
                })}
                label={t('user.status')}
              >
                <MenuItem value="true">{t('user.statuses.active')}</MenuItem>
                <MenuItem value="false">{t('user.statuses.inactive')}</MenuItem>
              </Select>
            </FormControl>

            {isEditMode && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.email_verified}
                    onChange={(e) => setFormData({
                      ...formData,
                      email_verified: e.target.checked
                    })}
                    name="email_verified"
                  />
                }
                label={t('user.emailVerified')}
                sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}
              />
            )}

            <TextField
              margin="dense"
              label={t('user.password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleFormChange}
              fullWidth
              required={!isEditMode} // Required only in add mode
              error={Boolean(formErrors.password)}
              helperText={formErrors.password || (isEditMode ? t('login.leaveBlankPassword') : '')}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="dense"
              label={t('user.confirmPassword')}
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleFormChange}
              fullWidth
              required={!isEditMode} // Required only in add mode
              error={Boolean(formErrors.confirmPassword)}
              helperText={formErrors.confirmPassword}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleCloseForm} 
              sx={{ 
                color: '#666666',  // Corporate dark gray
                '&:hover': {
                  backgroundColor: 'rgba(102, 102, 102, 0.1)',
                }, 
              }}
            >
              {t('user.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={createUserMutation.isPending || updateUserMutation.isPending}
              sx={{ 
                backgroundColor: '#003064',  // Corporate blue
                '&:hover': {
                  backgroundColor: '#00204d',  // Darker blue
                },
              }}
            >
              {createUserMutation.isPending || updateUserMutation.isPending ? <CircularProgress size={24} /> : t('user.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{
          backgroundColor: '#E19E19', // Corporate orange
          color: 'white',
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif'
        }}>
          {t('users.deleteUserConfirm')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText>
            {t('users.deleteUserMessage', { username: currentUser?.username })}
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('users.gracePeriodExplanation')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              color: '#666666',  // Corporate dark gray
              '&:hover': {
                backgroundColor: 'rgba(102, 102, 102, 0.1)',
              },
            }}
          >
            {t('user.cancel')}
          </Button>
          <Button
	            onClick={() => {
                handleDeleteUser();
              }}
            variant="contained"
            color="error"
            disabled={deleteUserMutation.isPending}
            sx={{
              backgroundColor: '#E19E19',  // Corporate orange
              '&:hover': {
                backgroundColor: '#c38400',  // Darker orange
              },
            }}
          >
            {deleteUserMutation.isPending ? <CircularProgress size={24} /> : t('users.deleteAccount')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Deletion Confirmation Dialog */}
      <Dialog open={cancelDeletionDialogOpen} onClose={handleCloseCancelDeletionDialog}>
        <DialogTitle sx={{
          backgroundColor: '#003064', // Corporate blue
          color: 'white',
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif'
        }}>
          {t('users.cancelDeletionConfirm')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText>
            {t('users.cancelDeletionMessage')}
            <br />
            <strong>{currentUser?.username}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseCancelDeletionDialog}
            sx={{
              color: '#666666',  // Corporate dark gray
              '&:hover': {
                backgroundColor: 'rgba(102, 102, 102, 0.1)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
	            onClick={() => {
                handleCancelDeletion();
              }}
            variant="contained"
            disabled={cancelDeletionMutation.isPending}
            sx={{
              backgroundColor: '#003064',  // Corporate blue
              '&:hover': {
                backgroundColor: '#002550',  // Darker blue
              },
            }}
          >
            {cancelDeletionMutation.isPending ? <CircularProgress size={24} /> : t('users.cancelDeletion')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Users;
